import { useEffect, useRef, useCallback, useState } from 'react';
import { leadershipTeam } from '../data/siteData';

function LeaderPortrait({ person, delayA, delayB, style }) {
  const [photoFailed, setPhotoFailed] = useState(false);

  return (
    <div style={style}>
      <div className="sg-frame sg-responsive-frame" style={styles.frame}>
        {!photoFailed ? (
          <img
            src={person.photo}
            alt={person.name || person.role}
            style={styles.photoImg}
            onError={() => setPhotoFailed(true)}
          />
        ) : (
          <span style={styles.initials}>{person.initials}</span>
        )}
        <div style={styles.frameOverlay} />
      </div>

      <div style={styles.namecard}>
        <div style={styles.namecardInner}>
          <h4 style={styles.namecardName}>{person.name}</h4>
          <p style={styles.namecardRole}>
            {person.role}
            {person.since ? `, ${person.since}` : ''}
          </p>
        </div>
      </div>

      <div style={{ ...styles.particle, top: -10, left: 20, animationDelay: delayA }} />
      <div style={{ ...styles.particle, top: 50, right: -5, animationDelay: delayB }} />
    </div>
  );
}

function LeadershipHero() {
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const mouseRef = useRef({ x: null, y: null });
  const particlesRef = useRef([]);
  const animFrameRef = useRef(null);

  const initParticles = useCallback((width, height) => {
    const particles = [];
    for (let i = 0; i < 60; i++) {
      particles.push({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.5,
        vy: (Math.random() - 0.5) * 0.5,
        size: Math.random() * 2 + 1,
        opacity: Math.random() * 0.5 + 0.2,
      });
    }
    particlesRef.current = particles;
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return undefined;
    const ctx = canvas.getContext('2d');
    let width, height;

    const resize = () => {
      const rect = container.getBoundingClientRect();
      width = canvas.width = rect.width;
      height = canvas.height = rect.height;
      initParticles(width, height);
    };

    const handleMouseMove = (e) => {
      const rect = container.getBoundingClientRect();
      mouseRef.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    };
    const handleMouseLeave = () => {
      mouseRef.current = { x: null, y: null };
    };

    window.addEventListener('resize', resize);
    container.addEventListener('mousemove', handleMouseMove);
    container.addEventListener('mouseleave', handleMouseLeave);
    resize();

    const connectionDistance = 150;
    const mouseDistance = 200;

    const animate = () => {
      ctx.clearRect(0, 0, width, height);
      const particles = particlesRef.current;
      const mouse = mouseRef.current;

      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > width) p.vx *= -1;
        if (p.y < 0 || p.y > height) p.vy *= -1;

        if (mouse.x != null) {
          const dx = mouse.x - p.x;
          const dy = mouse.y - p.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < mouseDistance) {
            const force = (mouseDistance - dist) / mouseDistance;
            p.vx += dx * force * 0.001;
            p.vy += dy * force * 0.001;
          }
        }

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(212,175,55,${p.opacity})`;
        ctx.fill();

        for (let j = i + 1; j < particles.length; j++) {
          const p2 = particles[j];
          const dx = p.x - p2.x;
          const dy = p.y - p2.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < connectionDistance) {
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.strokeStyle = `rgba(212,175,55,${0.15 * (1 - dist / connectionDistance)})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
      }
      animFrameRef.current = requestAnimationFrame(animate);
    };
    animate();

    return () => {
      window.removeEventListener('resize', resize);
      container.removeEventListener('mousemove', handleMouseMove);
      container.removeEventListener('mouseleave', handleMouseLeave);
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    };
  }, [initParticles]);

  const leaders = leadershipTeam.map((person, i) => ({
    person,
    delayA: `${i * 0.5}s`,
    delayB: `${i * 0.5 + 1}s`,
    transform: i === 0 ? 'translateY(0)' : i === 1 ? 'translateY(-30px)' : 'translateY(-60px)',
  }));

  const shapes = [
    { type: 'triangle', top: '10%', left: '5%', delay: '0s', scale: 1 },
    { type: 'hex', top: '20%', right: '8%', delay: '-5s', scale: 1 },
    { type: 'circle', bottom: '30%', left: '3%', delay: '-10s', scale: 1 },
    { type: 'diamond', top: '60%', right: '15%', delay: '-3s', scale: 1 },
    { type: 'triangle', bottom: '15%', left: '20%', delay: '-8s', scale: 0.6 },
    { type: 'hex', top: '40%', left: '45%', delay: '-12s', scale: 0.8 },
    { type: 'circle', top: '8%', left: '35%', delay: '-6s', scale: 0.5 },
    { type: 'diamond', bottom: '25%', right: '5%', delay: '-15s', scale: 0.7 },
  ];

  const scrollToValues = () => {
    document.querySelector('.speaks')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div ref={containerRef} style={styles.container}>
      <canvas ref={canvasRef} style={styles.canvas} />

      <div className="sg-shapes" style={styles.shapesLayer}>
        {shapes.map((s, i) => (
          <div
            key={i}
            style={{
              ...styles.shapeBase,
              top: s.top,
              left: s.left,
              right: s.right,
              bottom: s.bottom,
              transform: `scale(${s.scale})`,
              animationDelay: s.delay,
            }}
          >
            {s.type === 'triangle' && <div style={styles.triangle} />}
            {s.type === 'hex' && (
              <div style={styles.hex}>
                <div style={styles.hexBefore} />
                <div style={styles.hexAfter} />
              </div>
            )}
            {s.type === 'circle' && <div style={styles.circle} />}
            {s.type === 'diamond' && <div style={styles.diamond} />}
          </div>
        ))}
      </div>

      <div className="sg-rays" style={styles.rays} />

      <div style={styles.aurora}>
        <div style={{ ...styles.auroraWave, animationDelay: '0s' }} />
        <div style={{ ...styles.auroraWave, animationDelay: '-5s', opacity: 0.5 }} />
      </div>

      <div style={styles.noise} />

      <div className="sg-responsive-main" style={styles.main}>
        <div style={styles.logoArea}>
          <div style={styles.logo}>Sundaravel Group</div>
          <div style={styles.logoSub}>Est. 1969</div>
        </div>

        <div className="sg-responsive-content" style={styles.content}>
          <div style={styles.text}>
            <div style={styles.badge}>Leadership</div>
            <h1 className="sg-responsive-heading" style={styles.heading}>
              Our People:
              <br />
              <span style={styles.accent}>Vision</span> Drives
              <br />
              Excellence
            </h1>
            <p style={styles.paragraph}>
              Behind Sundaravel Group is a leadership team driven by vision, integrity, and a
              commitment to excellence. Their collective experience and forward-thinking approach
              continue to shape our businesses, empower our people, and build a stronger future.
            </p>
            <button type="button" style={styles.cta} onClick={scrollToValues}>
              Meet The Team
            </button>
          </div>

          <div className="sg-responsive-photos" style={styles.photos}>
            <svg style={styles.connectSvg} viewBox="0 0 800 100" preserveAspectRatio="none">
              <path style={styles.connectLine} d="M 0 50 Q 200 20 400 50 T 800 50" />
            </svg>

            {leaders.map(({ person, delayA, delayB, transform }, idx) => (
              <LeaderPortrait
                key={person.role}
                person={person}
                delayA={delayA}
                delayB={delayB}
                style={{
                  ...styles.person,
                  zIndex: 3 - idx,
                  transform,
                }}
              />
            ))}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes shapeFloat {
          0%, 100% { transform: translate(0,0) rotate(0deg); }
          25% { transform: translate(20px,-30px) rotate(5deg); }
          50% { transform: translate(-10px,20px) rotate(-3deg); }
          75% { transform: translate(15px,10px) rotate(2deg); }
        }
        @keyframes rayRotate {
          to { transform: rotate(360deg); }
        }
        @keyframes auroraMove {
          0%, 100% { transform: translateX(-25%) scaleY(1); }
          50% { transform: translateX(-30%) scaleY(1.1); }
        }
        @keyframes dashMove {
          to { stroke-dashoffset: -100; }
        }
        @keyframes particleFloat {
          0%, 100% { transform: translate(0,0); opacity: 0.4; }
          50% { transform: translate(10px,-20px); opacity: 0.8; }
        }

        .sg-frame {
          transition: all 0.4s ease;
        }
        .sg-frame:hover {
          transform: scale(1.05) translateY(-8px);
          box-shadow: 0 20px 60px rgba(0,0,0,0.4);
        }

        @media (max-width: 1100px) {
          .sg-responsive-main { padding: 40px 32px 0 !important; }
          .sg-responsive-content { flex-direction: column !important; align-items: flex-start !important; gap: 50px !important; }
          .sg-responsive-photos { justify-content: center !important; width: 100% !important; flex-wrap: wrap !important; gap: 32px !important; }
          .sg-responsive-person { transform: translateY(0) !important; }
          .sg-rays { display: none !important; }
        }
        @media (max-width: 700px) {
          .sg-responsive-main { padding: 32px 20px 0 !important; }
          .sg-responsive-photos { flex-direction: column !important; align-items: center !important; gap: 24px !important; }
          .sg-responsive-frame { width: 260px !important; height: 320px !important; }
          .sg-responsive-heading { font-size: 32px !important; }
          .sg-shapes { display: none !important; }
        }
      `}</style>
    </div>
  );
}

const styles = {
  container: {
    fontFamily: 'var(--font-body)',
    minHeight: '100vh',
    background: 'linear-gradient(105deg, #0a0e3d 0%, #0a0e3d 45%, #6b0a2e 75%, #8a0a32 100%)',
    position: 'relative',
    overflow: 'hidden',
    color: '#fff',
    WebkitFontSmoothing: 'antialiased',
    // Breaks out of the parent .section's 96px/24px padding so the panel
    // runs full browser width, matching the other full-bleed sections.
    width: '100vw',
    marginLeft: 'calc(50% - 50vw)',
    marginRight: 'calc(50% - 50vw)',
    marginTop: '-96px',
  },
  canvas: {
    position: 'absolute',
    inset: 0,
    zIndex: 0,
    pointerEvents: 'none',
  },
  shapesLayer: {
    position: 'absolute',
    inset: 0,
    zIndex: 0,
    pointerEvents: 'none',
    overflow: 'hidden',
  },
  shapeBase: {
    position: 'absolute',
    opacity: 0.06,
    animation: 'shapeFloat 20s ease-in-out infinite',
  },
  triangle: {
    width: 0,
    height: 0,
    borderLeft: '40px solid transparent',
    borderRight: '40px solid transparent',
    borderBottom: '70px solid #d4af37',
  },
  hex: {
    width: 60,
    height: 34,
    background: '#d4af37',
    position: 'relative',
  },
  hexBefore: {
    position: 'absolute',
    bottom: '100%',
    left: 0,
    width: 0,
    borderLeft: '30px solid transparent',
    borderRight: '30px solid transparent',
    borderBottom: '17px solid #d4af37',
  },
  hexAfter: {
    position: 'absolute',
    top: '100%',
    left: 0,
    width: 0,
    borderLeft: '30px solid transparent',
    borderRight: '30px solid transparent',
    borderTop: '17px solid #d4af37',
  },
  circle: {
    width: 80,
    height: 80,
    border: '2px solid #d4af37',
    borderRadius: '50%',
  },
  diamond: {
    width: 50,
    height: 50,
    background: '#d4af37',
    transform: 'rotate(45deg)',
  },
  rays: {
    position: 'absolute',
    top: '-20%',
    right: '-10%',
    width: 800,
    height: 800,
    zIndex: 0,
    pointerEvents: 'none',
    opacity: 0.08,
    background:
      'conic-gradient(from 180deg at 50% 50%, transparent 0deg, rgba(212,175,55,0.3) 10deg, transparent 20deg, transparent 40deg, rgba(212,175,55,0.2) 50deg, transparent 60deg, transparent 80deg, rgba(230,57,70,0.2) 90deg, transparent 100deg, transparent 120deg, rgba(212,175,55,0.15) 130deg, transparent 140deg, transparent 160deg, rgba(230,57,70,0.1) 170deg, transparent 180deg, transparent 200deg, rgba(212,175,55,0.1) 210deg, transparent 220deg, transparent 240deg, rgba(212,175,55,0.15) 250deg, transparent 260deg, transparent 280deg, rgba(230,57,70,0.1) 290deg, transparent 300deg, transparent 320deg, rgba(212,175,55,0.1) 330deg, transparent 340deg, transparent 360deg)',
    animation: 'rayRotate 60s linear infinite',
    borderRadius: '50%',
  },
  aurora: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: 300,
    zIndex: 0,
    pointerEvents: 'none',
    opacity: 0.15,
  },
  auroraWave: {
    position: 'absolute',
    bottom: 0,
    width: '200%',
    height: '100%',
    background:
      'radial-gradient(ellipse at 30% 100%, rgba(212,175,55,0.3) 0%, transparent 50%), radial-gradient(ellipse at 70% 100%, rgba(230,57,70,0.2) 0%, transparent 50%)',
    animation: 'auroraMove 15s ease-in-out infinite',
  },
  noise: {
    position: 'absolute',
    inset: 0,
    zIndex: 1,
    pointerEvents: 'none',
    opacity: 0.03,
    backgroundImage:
      "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
  },
  main: {
    position: 'relative',
    zIndex: 2,
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    padding: '48px 60px 0',
    maxWidth: 1400,
    margin: '0 auto',
  },
  logoArea: { marginBottom: 40 },
  logo: {
    fontSize: 32,
    fontWeight: 800,
    color: '#e63946',
    lineHeight: 1.1,
    marginBottom: 6,
    letterSpacing: -0.5,
  },
  logoSub: {
    fontSize: 14,
    fontWeight: 400,
    color: 'rgba(255,255,255,0.7)',
    lineHeight: 1.4,
  },
  content: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 60,
    paddingBottom: 40,
  },
  text: { maxWidth: 480, flexShrink: 0 },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    background: 'rgba(255,255,255,0.12)',
    padding: '8px 16px',
    borderRadius: 4,
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: '2px',
    textTransform: 'uppercase',
    color: 'rgba(255,255,255,0.9)',
    marginBottom: 28,
  },
  heading: {
    fontSize: 'clamp(36px, 4.5vw, 56px)',
    fontWeight: 600,
    lineHeight: 1.1,
    margin: '0 0 20px 0',
    letterSpacing: -1,
  },
  accent: { color: '#e63946' },
  paragraph: {
    fontSize: 16,
    lineHeight: 1.6,
    color: 'rgba(255,255,255,0.65)',
    margin: '0 0 32px 0',
  },
  cta: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 10,
    background: '#e63946',
    color: '#fff',
    padding: '14px 28px',
    borderRadius: 4,
    fontSize: 13,
    fontWeight: 600,
    letterSpacing: '1px',
    textTransform: 'uppercase',
    border: 'none',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
  },
  photos: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: 48,
    position: 'relative',
    flex: 1,
    justifyContent: 'flex-end',
    minHeight: 460,
    perspective: 1200,
  },
  person: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    transition: 'transform 0.4s ease',
  },
  frame: {
    position: 'relative',
    width: 240,
    height: 300,
    clipPath: 'polygon(15% 0, 100% 0, 100% 85%, 85% 100%, 0 100%, 0 15%)',
    overflow: 'hidden',
    // Transparent, not a fixed color: the cutout photos have transparent
    // backgrounds, and the panel's gradient shifts from navy to maroon
    // across its width, so any single hardcoded color shows as a visible
    // mismatched box on whichever portrait doesn't happen to sit on it.
    background: 'transparent',
    transition: 'all 0.4s ease',
  },
  photoImg: {
    position: 'absolute',
    inset: 0,
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    zIndex: 1,
  },
  corner: {
    position: 'absolute',
    width: 24,
    height: 24,
    zIndex: 4,
    pointerEvents: 'none',
    opacity: 0.7,
  },
  cornerTL: { top: 8, left: 8, borderTop: '2px solid #d4af37', borderLeft: '2px solid #d4af37' },
  cornerTR: { top: 8, right: 8, borderTop: '2px solid #d4af37', borderRight: '2px solid #d4af37' },
  cornerBL: {
    bottom: 8,
    left: 8,
    borderBottom: '2px solid #d4af37',
    borderLeft: '2px solid #d4af37',
  },
  cornerBR: {
    bottom: 8,
    right: 8,
    borderBottom: '2px solid #d4af37',
    borderRight: '2px solid #d4af37',
  },
  initials: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    fontSize: 52,
    fontWeight: 700,
    color: 'rgba(255,255,255,0.25)',
    letterSpacing: 6,
    zIndex: 2,
  },
  frameOverlay: {
    position: 'absolute',
    inset: 0,
    background: 'linear-gradient(180deg, transparent 40%, rgba(10,14,39,0.8) 100%)',
    zIndex: 2,
  },
  namecard: {
    position: 'relative',
    background: '#e8e4db',
    padding: '14px 22px',
    marginTop: -18,
    zIndex: 5,
    transform: 'skewX(-12deg)',
    minWidth: 220,
    borderLeft: '4px solid #e63946',
    transition: 'all 0.3s ease',
  },
  namecardInner: { transform: 'skewX(12deg)' },
  namecardName: {
    fontSize: 12,
    fontWeight: 700,
    color: '#4a0e1f',
    margin: '0 0 3px 0',
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
  },
  namecardRole: {
    fontSize: 11,
    color: '#6b5b4f',
    margin: 0,
    lineHeight: 1.4,
  },
  particle: {
    position: 'absolute',
    width: 4,
    height: 4,
    background: '#d4af37',
    borderRadius: '50%',
    opacity: 0.4,
    animation: 'particleFloat 4s ease-in-out infinite',
  },
  connectSvg: {
    position: 'absolute',
    top: '30%',
    left: '10%',
    right: '5%',
    height: 1,
    pointerEvents: 'none',
    zIndex: 0,
  },
  connectLine: {
    stroke: 'rgba(212,175,55,0.2)',
    strokeWidth: 1,
    strokeDasharray: '4 4',
    fill: 'none',
    animation: 'dashMove 20s linear infinite',
  },
};

export default LeadershipHero;
